<template>
  <div>
    <!-- URL Input Form -->
    <CCard class="mb-0">
      <CCardHeader class="p-3 d-flex flex-column justify-content-between flex-sm-row align-items-start align-items-sm-center flex-gap-3">
        <h6 class="m-0">Enter Menu URL to Scrape</h6>
        <div class="vertical-center flex-gap-3">
          <div class="vertical-center flex-gap-1">
            <span>Use Proxy</span>
            <CSwitch
              color="info"
              :checked.sync="useProxy"
              size="sm"
              shape="pill"
            />
          </div>
          <div v-if="isTakeaway" class="vertical-center flex-gap-1">
            <span>Use CDN</span>
            <CSwitch
              color="info"
              :checked.sync="useCdn"
              size="sm"
              shape="pill"
            />
          </div>
        </div>
      </CCardHeader>

      <CCardBody class="p-3">
        <CForm @submit.prevent="scrapeMenu" class="menu-scraper-form d-flex flex-column flex-md-row flex-gap-2">
          <CInput
            type="url"
            v-model="url"
            class="flex-grow-1 mb-0"
            placeholder="https://"
            required
            size="lg"
          />
          <CButton
            color="primary"
            type="submit"
            :disabled="loading"
            size="lg"
            class="align-self-center d-flex align-items-center flex-gap-2"
          >
            <CSpinner v-if="loading" size="sm" />
            <span>{{ loading ? 'Scraping...' : 'Scrape Menu' }}</span>
          </CButton>
        </CForm>
      </CCardBody>
    </CCard>

    <!-- Loading Indicator -->
    <div v-if="loading" class="text-center my-5">
      <CSpinner color="primary" />
      <div class="mt-3">Scraping menu data, please wait...</div>
    </div>

    <!-- Results -->
    <div v-if="menuData && !loading">
      <!-- Restaurant Information -->
      <CCard class="my-3">
        <CCardBody>
          <div class="d-flex flex-column flex-md-row align-items-center">
            <div class="restaurant-logo mb-3 mb-md-0 mr-md-4">
              <img
                v-if="menuData.menu.restaurantInfo.logoUrl"
                :src="menuData.menu.restaurantInfo.logoUrl"
              />
              <div v-else class="placeholder-logo">No Logo</div>
            </div>

            <div class="flex-grow-1">
              <h4>{{ menuData.menu.restaurantInfo.name }}</h4>
              <div class="restaurant-details">
                <div><strong>Email:</strong> {{ menuData.menu.restaurantInfo.email || '-' }}</div>
                <div><strong>VAT:</strong> {{ menuData.menu.restaurantInfo.vatNumber || 'N/A' }}</div>
                <div v-if="menuData.menu.restaurantInfo.location">
                  <strong>Address:</strong>
                  {{ menuData.menu.restaurantInfo.location.address }},
                  {{ menuData.menu.restaurantInfo.location.postalCode }}
                  {{ menuData.menu.restaurantInfo.location.city }}
                </div>
                <div v-if="menuData.menu.restaurantInfo.cuisineTypes && menuData.menu.restaurantInfo.cuisineTypes.length">
                  <strong>Cuisine: </strong>
                  <CBadge
                    v-for="(cuisine, index) in menuData.menu.restaurantInfo.cuisineTypes"
                    :key="'cuisine-' + index"
                    color="info"
                    class="mr-1 mb-1"
                  >
                    {{ cuisine.name }}
                  </CBadge>
                </div>
              </div>
            </div>

            <div v-if="menuData.menu.restaurantInfo.bannerUrl" class="banner-preview mt-3 mt-md-0">
              <img
                :src="menuData.menu.restaurantInfo.bannerUrl"
                alt="Restaurant Banner"
              />
            </div>
          </div>
        </CCardBody>
      </CCard>

      <!-- Categories and Menu Items -->
      <CCard class="mb-0">
        <CCardHeader class="d-flex flex-column flex-md-row align-items-md-center flex-wrap p-3">
          <div
            v-if="showOptionGroupsWarning"
            class="alert alert-warning d-flex align-items-center flex-gap-3 w-100 mb-4"
          >
            <CIcon name="cil-warning" size="xl" class="text-warning flex-shrink-0" />
            <div>
              <strong>Warning:</strong>
              Some items have option groups defined, but option group details could not be loaded. Options and option groups will not be imported for these items.
            </div>
          </div>

          <div>
            <h6 class="m-0">
              Categories & Menu Items
              <small class="text-muted ml-2">
                ({{ selectedItemCount }} items selected)
              </small>
            </h6>
            <div class="mt-2 d-flex align-items-center">
              <CSwitch
                class="mr-2"
                color="success"
                :checked.sync="selectAllCategories"
                @update:checked="toggleAllCategories"
                shape="pill"
              />
              <span>Select all categories</span>
            </div>
          </div>

          <div class="ml-md-auto mt-3 mt-md-0 d-flex flex-column flex-md-row">
            <CButton
              color="info"
              @click="showRestaurantImportModal = true"
              class="d-flex align-items-center mb-2 mb-md-0 mr-md-2 justify-content-center"
              :disabled="!menuData"
            >
              <CIcon name="cil-restaurant" class="mr-1" /> Import Restaurant Info
            </CButton>

            <CButton
              color="primary"
              :disabled="selectedItemCount === 0"
              @click="showMenuImportModal = true"
              class="d-flex align-items-center justify-content-center"
            >
              <CIcon name="cil-vertical-align-bottom" class="mr-1" /> Import Selected Items
            </CButton>
          </div>
        </CCardHeader>

        <CCardBody class="p-0">
          <CListGroup>
            <CListGroupItem
              v-for="category in menuData.menu.categories"
              :key="'cat-' + category.data_id"
              class="menu-category py-3"
              :class="{ 'selected': selectedCategories[category.data_id] }"
            >
              <div class="d-flex align-items-center" role="button" @click="toggleCategoryCollapse(category.data_id)">
                <CSwitch
                  class="mr-2"
                  color="success"
                  :checked.sync="selectedCategories[category.data_id]"
                  @update:checked="toggleCategory(category.data_id)"
                  shape="pill"
                  size="sm"
                />
                <h6 class="m-0">
                  {{ category.category_name }}
                  <small class="text-muted ml-2">
                    ({{ getCategoryItems(category.data_id).length }} items)
                  </small>
                </h6>

                <CIcon class="ml-auto mr-1" :name="categoryCollapse[category.data_id] ? 'cil-chevron-bottom' : 'cil-chevron-right'" />
              </div>

              <div v-if="category.note_public" class="mt-1 text-muted font-italic">
                <small>{{ category.note_public }}</small>
              </div>

              <CCollapse :show="categoryCollapse[category.data_id]">
                <CListGroup v-if="categoryCollapse[category.data_id]" flush>
                  <CListGroupItem
                    v-for="item in getCategoryItems(category.data_id)"
                    :key="'item-' + item.data_id"
                  >
                  <div class="d-flex align-items-center justify-content-between">
                    <CSwitch
                      class="mr-2"
                      color="success"
                      :checked.sync="selectedItems[item.data_id]"
                      @update:checked="toggleItem(category.data_id)"
                      shape="pill"
                      size="sm"
                    />

                    <div>
                      <h6 class="m-0">{{ item.menu_name }}</h6>
                      <div v-if="item.description" class="text-muted small">{{ item.description }}</div>
                      <div v-if="hasVariations(item)">
                        <CBadge color="info" class="mr-1">{{ getVariationCount(item) }} variation(s)</CBadge>
                        <CBadge color="warning" role="button" v-if="hasOptionGroups(item)" @click.stop="showOptionGroupsSidebar(item)">Has option groups →</CBadge>
                      </div>
                    </div>

                    <div class="ml-auto">
                      <strong>{{ getBasePrice(item) | toCurrency }}</strong>
                    </div>
                  </div>
                </CListGroupItem>
                </CListGroup>
              </CCollapse>

            </CListGroupItem>
          </CListGroup>
        </CCardBody>
      </CCard>
    </div>


    <!-- Menu Import Modal -->
    <CModal
      :show.sync="showMenuImportModal"
      title="Import Menu Items"
      color="white"
      size="lg"
      :centered="false"
      :closeOnBackdrop="false"
    >
      <CForm class="menu-scraper-form">
        <div v-if="!restaurantsLoading && allRestaurants.length > 0">
          <h6>Select a restaurant to import the menu items:</h6>
          <div class="my-3">
            <v-select
              v-model="selectedRestaurant"
              :options="allRestaurants"
              :reduce="option => option.value"
              label="label"
              placeholder="Select a restaurant..."
              class="mb-3 v-select-filter"
            />

            <div class="alert alert-info">
              <strong>Selected Items:</strong> {{ selectedItemCount }} items from {{ selectedCategoryCount }} categories
            </div>

            <div class="form-group d-flex align-items-center">
              <CSwitch
                class="mr-2"
                color="primary"
                :checked.sync="importOptions.importOptionGroups"
                shape="pill"
                size="sm"
              />
              <span>Import option groups and options</span>
            </div>
          </div>
        </div>

        <div v-else-if="restaurantsLoading" class="text-center my-3">
          <CSpinner color="primary" size="sm" />
          <span class="ml-2">Loading restaurants...</span>
        </div>
      </CForm>

      <template #footer>
        <CButton @click="showMenuImportModal = false" color="link">Close</CButton>

        <CButton
          color="primary"
          shape="pill"
          :disabled="!selectedRestaurant || importing"
          @click="importMenu"
        >
          <CSpinner v-if="importing" size="sm" />
          {{ importing ? 'Importing...' : 'Import Selected Items' }}
        </CButton>
      </template>
    </CModal>

    <!-- Restaurant Import Modal -->
    <CModal
      :show.sync="showRestaurantImportModal"
      title="Import Restaurant Information"
      color="white"
      size="lg"
      :centered="false"
      :closeOnBackdrop="false"
    >
      <CForm class="menu-scraper-form">
        <div v-if="menuData && menuData.menu.restaurantInfo">

          <CCard>
            <CCardBody class="p-2">
              <div class="d-flex flex-column flex-md-row align-items-md-center">
                <div class="restaurant-logo mb-3 mb-md-0 mr-md-4">
                  <img
                    v-if="menuData.menu.restaurantInfo.logoUrl"
                    :src="menuData.menu.restaurantInfo.logoUrl"
                  />
                  <div v-else class="placeholder-logo">No Logo</div>
                </div>

                <div>
                  <h5 class="mb-2">{{ menuData.menu.restaurantInfo.name }}</h5>
                  <div class="text-muted">Source: {{ menuData.host }}</div>
                </div>
              </div>
            </CCardBody>
          </CCard>

          <div class="form-group mb-4">
            <!-- Email -->
            <div v-if="false" class="d-flex align-items-center mb-3">
              <CSwitch
                class="mr-2"
                color="primary"
                :checked.sync="restaurantImportOptions.importEmail"
                :disabled="!menuData.menu.restaurantInfo.email"
                shape="pill"
                size="sm"
              />
              <div>
                <strong>Email:</strong> {{ menuData.menu.restaurantInfo.email || 'Not available' }}
              </div>
            </div>

            <!-- VAT -->
            <div class="d-flex align-items-center mb-3">
              <CSwitch
                class="mr-2"
                color="primary"
                :checked.sync="restaurantImportOptions.importVAT"
                :disabled="!menuData.menu.restaurantInfo.vatNumber"
                shape="pill"
                size="sm"
              />
              <div>
                <strong>VAT:</strong> {{ menuData.menu.restaurantInfo.vatNumber || 'Not available' }}
              </div>
            </div>

            <!-- Address -->
            <div v-if="false" class="d-flex align-items-center mb-3">
              <CSwitch
                class="mr-2"
                color="primary"
                :checked.sync="restaurantImportOptions.importAddress"
                :disabled="!menuData.menu.restaurantInfo.location"
                shape="pill"
                size="sm"
              />
              <div>
                <strong>Address:</strong>
                <span v-if="menuData.menu.restaurantInfo.location">
                  {{ menuData.menu.restaurantInfo.location.address }},
                  {{ menuData.menu.restaurantInfo.location.postalCode }}
                  {{ menuData.menu.restaurantInfo.location.city }}
                </span>
                <span v-else>Not available</span>
              </div>
            </div>

            <!-- Logo -->
            <div class="d-flex align-items-center mb-3">
              <CSwitch
                class="mr-2"
                color="primary"
                :checked.sync="restaurantImportOptions.importLogo"
                :disabled="!menuData.menu.restaurantInfo.logoUrl"
                shape="pill"
                size="sm"
              />
              <div>
                <strong>Logo: </strong>
                <span v-if="menuData.menu.restaurantInfo.logoUrl">Available</span>
                <span v-else>Not available</span>
              </div>
            </div>
          </div>

          <div class="form-group mb-0">
            <h6>Select restaurant information to import:</h6>
            <v-select
              v-model="selectedRestaurant"
              :options="allRestaurants"
              :reduce="option => option.value"
              label="label"
              placeholder="Select a restaurant..."
              class="mb-3 v-select-filter"
            />
          </div>
        </div>

        <div v-else-if="restaurantsLoading" class="text-center my-3">
          <CSpinner color="primary" size="sm" />
          <span class="ml-2">Loading restaurants...</span>
        </div>
      </CForm>

      <template #footer>
        <CButton @click="showRestaurantImportModal = false" color="link">Close</CButton>

        <CButton
          color="primary"
          shape="pill"
          :disabled="!selectedRestaurant || importing || !Object.values(restaurantImportOptions).some(val => val === true)"
          @click="importRestaurant"
        >
          <CSpinner v-if="importing" size="sm" />
          {{ importing ? 'Importing...' : 'Import Restaurant Info' }}
        </CButton>
      </template>
    </CModal>

    <!-- Option Groups Sidebar -->
    <mc-spinner :opacity="0.8" v-show="loadingOptionGroupsSidebar" :mtop="(250 + mcSpinnerMarginTop) + 'px'" />
    <OptionGroupsSidebar :item="selectedMenuItem" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import _debounce from 'lodash/debounce';
import OptionGroupsSidebar from './OptionGroupsSidebar.vue';

export default {
  name: 'MenuScraper',
  components: {
    OptionGroupsSidebar,
  },

  data() {
    return {
      url: '',
      useProxy: false,
      useCdn: true,

      loading: false,
      menuData: null,
      selectedCategories: {},
      selectedItems: {},
      categoryCollapse: {},
      selectAllCategories: false,
      menuIndexes: {
        itemsByCategory: {},
        itemById: {},
        categoryById: {},
        optionGroupById: {},
        optionById: {},
      },

      showMenuImportModal: false,
      showRestaurantImportModal: false,
      selectedRestaurant: null,
      restaurantsLoading: false,
      importing: false,
      importOptions: {
        importOptionGroups: true
      },

      restaurantImportOptions: {
        importEmail: false,
        importVAT: false,
        importAddress: false,
        importLogo: false
      },

      selectedMenuItem: null,
      loadingOptionGroupsSidebar: false,
      mcSpinnerMarginTop: 0,
    };
  },

  computed: {
    ...mapGetters(['errors']),
    ...mapGetters('restaurants', ['allRestaurants']),

    isTakeaway() {
      if (!this.url) return false;
      return this.url.toLowerCase().includes('takeaway.com');
    },

    showOptionGroupsWarning() {
      if (!this.menuData || !this.menuData.menu) return false;

      const hasItemsWithOptionGroups = this.menuData.menu.menuItems &&
        this.menuData.menu.menuItems.some(item => this.hasOptionGroups(item));

      const hasNoOptionDefinitions =
        (!this.menuData.menu.options || this.menuData.menu.options.length === 0) &&
        (!this.menuData.menu.optionGroups || this.menuData.menu.optionGroups.length === 0) &&
        (!this.menuData.menu.optionGroupOptions || this.menuData.menu.optionGroupOptions.length === 0);

      return hasItemsWithOptionGroups && hasNoOptionDefinitions;
    },

    selectedItemCount() {
      return Object.values(this.selectedItems).reduce((count, isSelected) => count + (isSelected ? 1 : 0), 0);
    },

    selectedCategoryCount() {
      return Object.values(this.selectedCategories).reduce((count, isSelected) => count + (isSelected ? 1 : 0), 0);
    },
  },

  async mounted() {
    await this.loadRestaurants();

    window.onscroll = () => {
      this.mcSpinnerMarginTop = document.documentElement.scrollTop;
    }
  },

  methods: {
    ...mapActions('restaurants', ['fetchAllRestaurants']),

    showOptionGroupsSidebar(item) {
      this.loadingOptionGroupsSidebar = true;

      setTimeout(() => {
        // Option group ve option'ları ekle
        const { optionGroups, options } = this.getOptionGroupsAndOptionsForItem(item);

        this.selectedMenuItem = {
          ...item,
          option_groups: optionGroups,
          options: options
        };
        this.$store.commit('set', ['optionGroupsAsideShow', true]);
        this.loadingOptionGroupsSidebar = false;
      }, 600);
    },

    async loadRestaurants() {
      this.restaurantsLoading = true;
      await this.fetchAllRestaurants();
      this.restaurantsLoading = false;
    },

    async scrapeMenu() {
      this.loading = true;
      this.menuData = null;
      this.selectedCategories = {};
      this.selectedItems = {};
      this.categoryCollapse = {};
      this.resetMenuIndexes();

      try {
        const params = {
          url: this.url,
          proxy: this.useProxy ? 1 : 0,
          screenshot: 0
        };

        if (this.isTakeaway && this.useCdn) {
          params.useCdn = 1;
        }

        const response = await this.$axios.get(this.$backend.SCRAPER.MENU.SCRAPE, {
          params
        });

        this.menuData = response.data;
        this.buildMenuIndexes();

        // Initialize category collapse states
        this.menuData.menu.categories.forEach(category => {
          this.$set(this.categoryCollapse, category.data_id, false);
          this.$set(this.selectedCategories, category.data_id, false);
        });

        // Initialize menu item selection states
        this.menuData.menu.menuItems.forEach(item => {
          this.$set(this.selectedItems, item.data_id, false);
        });

      } catch (error) {
        this.$toast.error(error.response?.data?.error || 'Failed to scrape menu. Please try again.');
      } finally {
        this.loading = false;
      }
    },

    resetMenuIndexes() {
      this.menuIndexes = {
        itemsByCategory: {},
        itemById: {},
        categoryById: {},
        optionGroupById: {},
        optionById: {},
      };
    },

    buildMenuIndexes() {
      if (!this.menuData || !this.menuData.menu) return;

      const menu = this.menuData.menu;
      const itemsByCategory = {};
      const itemById = {};
      const categoryById = {};
      const optionGroupById = {};
      const optionById = {};

      (menu.categories || []).forEach(category => {
        categoryById[category.data_id] = category;
        itemsByCategory[category.data_id] = [];
      });

      (menu.menuItems || []).forEach(item => {
        itemById[item.data_id] = item;
      });

      (menu.menuCategories || []).forEach(link => {
        const item = itemById[link.menu_id];
        if (!item) return;

        if (!itemsByCategory[link.category_id]) {
          itemsByCategory[link.category_id] = [];
        }

        itemsByCategory[link.category_id].push(item);
      });

      Object.keys(itemsByCategory).forEach(categoryId => {
        itemsByCategory[categoryId].sort((a, b) => (a.menu_index || 0) - (b.menu_index || 0));
      });

      (menu.optionGroups || []).forEach(group => {
        optionGroupById[group.data_id] = group;
      });

      (menu.options || []).forEach(option => {
        optionById[option.data_id] = option;
      });

      this.menuIndexes = {
        itemsByCategory,
        itemById,
        categoryById,
        optionGroupById,
        optionById,
      };
    },

    getCategoryItems(categoryId) {
      return this.menuIndexes.itemsByCategory[categoryId] || [];
    },

    getItemsInCategory(categoryId) {
      return this.getCategoryItems(categoryId);
    },

    toggleCategory(categoryId) {
      const items = this.getItemsInCategory(categoryId);
      const selected = this.selectedCategories[categoryId];

      // Toggle all items in this category
      items.forEach(item => {
        this.$set(this.selectedItems, item.data_id, selected);
      });

      // Open or close category
      this.$set(this.categoryCollapse, categoryId, selected);
    },

    toggleAllCategories() {
      if (!this.menuData) return;

      this.menuData.menu.categories.forEach(category => {
        this.$set(this.selectedCategories, category.data_id, this.selectAllCategories);
        this.toggleCategory(category.data_id);
      });
    },

    toggleCategoryCollapse(categoryId) {
      this.$set(this.categoryCollapse, categoryId, !this.categoryCollapse[categoryId]);
    },

    toggleItem(categoryId) {
      const itemsInCategory = this.getItemsInCategory(categoryId);

      const anySelected = itemsInCategory.some(i => this.selectedItems[i.data_id]);

      this.$set(this.selectedCategories, categoryId, anySelected);
    },

    hasVariations(item) {
      return item.variations && item.variations.length > 0;
    },

    getVariationCount(item) {
      return item.variations ? item.variations.length : 0;
    },

    hasOptionGroups(item) {
      if (!item.variations || !item.variations.length) return false;

      return item.variations.some(variation =>
        variation.option_groups && variation.option_groups.length > 0
      );
    },

    getBasePrice(item) {
      if (!item.variations || !item.variations.length) return 0;

      const price = item.variations[0].basePrice;
      return price;
    },

    getSelectedItems() {
      return Object.keys(this.selectedItems)
        .filter(itemId => this.selectedItems[itemId])
        .map(itemId => this.menuIndexes.itemById[itemId])
        .filter(Boolean);
    },

    getSelectedCategories() {
      return Object.keys(this.selectedCategories)
        .filter(categoryId => this.selectedCategories[categoryId])
        .map(categoryId => this.menuIndexes.categoryById[categoryId])
        .filter(Boolean);
    },

    getOptionGroupsAndOptionsForItem(item) {
      // Seçili menu item için option group ve option listelerini döndürür
      let optionGroups = [];
      let options = [];
      if (item.variations) {
        item.variations.forEach(variation => {
          if (variation.option_groups) {
            variation.option_groups.forEach(groupId => {
              const group = this.menuIndexes.optionGroupById[groupId];
              if (group && !optionGroups.some(og => og.data_id === group.data_id)) {
                // Optionları da ekle
                let groupOptions = [];
                if (group.options) {
                  group.options.forEach(optionId => {
                    const option = this.menuIndexes.optionById[optionId];
                    if (option && !options.some(o => o.data_id === option.data_id)) {
                      options.push(option);
                    }
                    if (option && !groupOptions.some(o => o.data_id === option.data_id)) {
                      groupOptions.push(option);
                    }
                  });
                }
                optionGroups.push({ ...group, options: groupOptions });
              }
            });
          }
        });
      }
      return { optionGroups, options };
    },

    async importMenu() {
      if (!this.selectedRestaurant) return;

      this.importing = true;

      try {
        const selectedCategories = this.getSelectedCategories();
        const selectedItems = this.getSelectedItems();

        // Find related option groups and options for selected items
        let selectedOptionGroups = [];
        let selectedOptions = [];

        if (this.importOptions.importOptionGroups) {
          // Get all option groups related to selected items
          selectedItems.forEach(item => {
            const { optionGroups, options } = this.getOptionGroupsAndOptionsForItem(item);
            optionGroups.forEach(group => {
              if (!selectedOptionGroups.some(og => og.data_id === group.data_id)) {
                selectedOptionGroups.push(group);
              }
            });
            options.forEach(option => {
              if (!selectedOptions.some(o => o.data_id === option.data_id)) {
                selectedOptions.push(option);
              }
            });
          });
        }

        // Prepare payload
        const payload = {
          restaurant_id: this.selectedRestaurant,
          menuData: {
            categories: selectedCategories,
            menuItems: selectedItems,
            optionGroups: this.importOptions.importOptionGroups ? selectedOptionGroups : [],
            options: this.importOptions.importOptionGroups ? selectedOptions : [],
          },
          preferences: this.importOptions,
          host: this.menuData.host,
          url: this.url,
        };

        // Send import request
        await this.$axios.post(this.$backend.SCRAPER.MENU.IMPORT, payload);

        // Handle successful import
        this.$toast.success('Menu items imported successfully!');
        this.showMenuImportModal = false;

        // this.$router.push({
        //   name: 'View Restaurant',
        //   params: { id: this.selectedRestaurant }
        // });

      } catch (error) {
        this.$toast.error(
          typeof this.errors === 'object'
            ? Object.values(this.errors).join()
            : (this.errors || 'Failed to import menu. Please try again.')
        );
      } finally {
        this.importing = false;
      }
    },

    async importRestaurant() {
      if (!this.selectedRestaurant || !Object.values(this.restaurantImportOptions).some(val => val === true)) {
        return;
      }

      this.importing = true;

      try {
        // Prepare the restaurant data to be imported
        const restaurantData = {
          restaurant_id: this.selectedRestaurant,
          data: {
            name: this.menuData.menu.restaurantInfo.name
          }
        };

        // Add selected information based on user choices
        if (this.restaurantImportOptions.importEmail && this.menuData.menu.restaurantInfo.email) {
          restaurantData.data.email = this.menuData.menu.restaurantInfo.email;
        }

        if (this.restaurantImportOptions.importVAT && this.menuData.menu.restaurantInfo.vatNumber) {
          restaurantData.data.vatNumber = this.menuData.menu.restaurantInfo.vatNumber;
        }

        if (this.restaurantImportOptions.importAddress && this.menuData.menu.restaurantInfo.location) {
          restaurantData.data.location = this.menuData.menu.restaurantInfo.location;
        }

        if (this.restaurantImportOptions.importLogo && this.menuData.menu.restaurantInfo.logoUrl) {
          restaurantData.data.logoUrl = this.menuData.menu.restaurantInfo.logoUrl;
        }

        // Include source information
        restaurantData.host = this.menuData.host;

        // This is a placeholder for the actual API endpoint
        // You'll need to implement the backend endpoint
        await this.$axios.post(this.$backend.SCRAPER.RESTAURANT.IMPORT_INFO, restaurantData);

        // Handle successful import
        this.$toast.success('Restaurant information imported successfully!');
        this.showRestaurantImportModal = false;

      } catch (error) {
        this.$toast.error(
          typeof this.errors === 'object'
            ? Object.values(this.errors).join()
            : (this.errors || 'Failed to import restaurant information. Please try again.')
        );
      } finally {
        this.importing = false;
      }
    }
  }
};
</script>

<style scoped>
.menu-category {
  border-left: 4px solid #39f;
}
.menu-category.selected {
  border-left-color: #2eb85c;
}

.placeholder-logo {
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f3f5;
  color: #73818f;
  font-weight: bold;
  border-radius: 4px;
}

.banner-preview > img {
  max-width: 100%;
  max-height: 120px;
  object-fit: cover;
  border-radius: 4px;
}

.restaurant-logo > img {
  max-width: 100px;
  max-height: 100px;
  border-radius: 4px;
}
</style>

<style>
.menu-scraper-form .form-control {
  /* font-size: 1.025rem; */
  color: #9b0a0a;
  font-weight: 500;
  padding: 0.375rem 0.375rem;
}
</style>

